import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { composeSwapTxByAmountFrom, useGetSwapQuotesByAmountFrom } from '@/api/pactswap/generated/api';
import {
  ComposeSwapTxByAmountFrom200,
  GetSwapQuotesByAmountFrom200ItemFees,
  GetSwapQuotesByAmountFromFromType,
  GetSwapQuotesByAmountFromToType,
} from '@/api/pactswap/generated/api.schemas';
import { Currency, ENV, getPreset } from '@/constants';
import { addToBlackList, getBlackList } from '@/hooks/swap/blackList';
import { useSwapLimitsSatisfied } from '@/hooks/swap/useSwapLimitsSatisfied';
import useL1TokenContractParams from '@/hooks/useL1TokenContractParams';
import { useTokenUsdPrices } from '@/hooks/useTokenUsdPrices';
import { convertBigIntToString, decimalsForCurrency, isBtcLikeCurrency } from '@/utils';
import { formatNumberWithPrecision } from '@/utils';
import Big from '@/utils/Bigjs';
import { findTokenCombinationFromUrl } from '@/utils/findTokenCombinationFromUrl';

type SwapQuote = {
  fees: GetSwapQuotesByAmountFrom200ItemFees;
  amountToRaw: bigint;
  amountToHumanReadable: string;
  amountToUsd: string;
  liquidityProvider: string;
  minAmountTo?: string;
};

type AmountFrom = {
  amountFromRaw: bigint;
  amountFromHumanReadable: string;
  amountFromUsd: string;
};

type AnyNumber = string | number | bigint;

type GetSwapTransactionParams = {
  fromWalletAddress: string;
  fromPublicKey?: string;
  utxos?: Array<{ txid: string; vout: number; value: number }>;
};

type SwapContextType = {
  tokenFrom: Currency;
  tokenTo: Currency;
  amountFrom: AmountFrom;
  swapQuotes: SwapQuote[];
  swapQuotesError: Error | null;
  liquidityProvider: string | null;
  isLoadingSwapQuotes: boolean;
  receiverAddress: string;
  isFiatInput: boolean;
  slippageBips: number;
  setReceiverAddress: (receiverAddress: string) => void;
  setLiquidityProvider: (liquidityProvider: string) => void;
  setTokenFrom: (token: Currency) => void;
  setTokenTo: (token: Currency) => void;
  setAmountFrom: (amount: AnyNumber) => void;
  setIsFiatInput: (is: boolean) => void;
  getSwapTransaction: (params: GetSwapTransactionParams) => Promise<ComposeSwapTxByAmountFrom200>;
  isLimitsSatisfied: boolean;
  addOrderToBlackList: (currency: Currency, orderId: string) => void;
  setSlippageBips: (newSlippage: number) => void;
};

export const SwapContext = createContext<SwapContextType | null>(null);

export const useSwapContext = () => {
  const context = useContext(SwapContext);
  if (context === null) {
    throw new Error('useSwapContext must be used within a SwapContextProvider');
  }
  return context;
};

const FIAT_PRECISION = 2;
const MAXIMUM_TOKEN_DECIMAL_PRECISION = 8;

export const SwapContextProvider = ({ children }: { children: React.ReactNode }) => {
  // Set initial token combination from URL
  const [firstToken = Currency.ETH, secondToken = Currency.BNB] = useRef(
    findTokenCombinationFromUrl(window.location.pathname)
  ).current;

  const { getContractParams } = useL1TokenContractParams();
  const { data: allPrices, getTokenUsdPrice } = useTokenUsdPrices();

  const [tokenFrom, setTokenFrom] = useState<Currency>(firstToken);
  const [tokenTo, setTokenTo] = useState<Currency>(secondToken);
  const [liquidityProvider, setLiquidityProvider] = useState<string | null>(null);
  const [receiverAddress, setReceiverAddress] = useState<string>('');
  const [isFiatInput, setIsFiatInput] = useState<boolean>(false);

  const [amountFrom, setAmountFrom] = useState<AmountFrom>({
    amountFromRaw: 0n,
    amountFromHumanReadable: '0',
    amountFromUsd: '0.00',
  });

  const [slippageBips, setSlippageBips] = useState<number>(Number(ENV['VITE_SLIPPAGE_BIPS']));

  const isLimitsSatisfied = useSwapLimitsSatisfied(tokenFrom, tokenTo, amountFrom.amountFromRaw);

  const {
    data: swapQuotesData = [],
    error: swapQuotesError,
    isLoading: isLoadingSwapQuotes,
    isFetching: isFetchingSwapQuotes,
    refetch,
  } = useGetSwapQuotesByAmountFrom(
    {
      from: getContractParams(tokenFrom).L2_CONTRACT_ADDRESS_BASE,
      to: getContractParams(tokenTo).L2_CONTRACT_ADDRESS_MAKER,
      amountFrom: String(amountFrom.amountFromRaw),
      fromType: tokenFrom.toLowerCase() as GetSwapQuotesByAmountFromFromType,
      toType: tokenTo.toLowerCase() as GetSwapQuotesByAmountFromToType,
      excludeC1OrdersIds: isBtcLikeCurrency(tokenFrom) ? Array.from(getBlackList(tokenFrom)) : undefined,
      slippageBips,
    },
    {
      query: {
        enabled: !!tokenFrom && !!tokenTo && !!amountFrom.amountFromRaw && isLimitsSatisfied,
        refetchInterval: 5000,
        select: (data) => {
          const quotes = data.map((quote) => {
            const amountToRaw = BigInt(quote.amountTo ?? 0n);
            const tokenValueToken = formatNumberWithPrecision(
              Number(convertBigIntToString(amountToRaw, tokenTo)),
              MAXIMUM_TOKEN_DECIMAL_PRECISION
            );

            const minAmountTo =
              quote.minAmountTo !== undefined
                ? formatNumberWithPrecision(
                    convertBigIntToString(BigInt(quote.minAmountTo), tokenTo),
                    MAXIMUM_TOKEN_DECIMAL_PRECISION
                  )
                : undefined;

            return {
              amountToRaw,
              amountToHumanReadable: tokenValueToken,
              amountToUsd: quote.usdAmounts.to.toFixed(FIAT_PRECISION),
              liquidityProvider: quote.lp ?? '',
              minAmountTo,
              fees: quote.fees,
            };
          });
          return quotes;
        },
      },
    }
  );

  useEffect(() => {
    if (isFetchingSwapQuotes) {
      return;
    }

    if (swapQuotesData.length && !liquidityProvider) {
      const bestQuote = swapQuotesData.sort((a, b) => Number(b.amountToRaw) - Number(a.amountToRaw))[0];
      setLiquidityProvider(bestQuote.liquidityProvider);
    } else if (!swapQuotesData.length) {
      setLiquidityProvider(null);
    }
  }, [isFetchingSwapQuotes, liquidityProvider]);

  useEffect(() => {
    if (amountFrom.amountFromRaw > 0n) {
      setAmountFromHandler(amountFrom.amountFromHumanReadable);
    }
  }, [allPrices, tokenFrom]);

  const setAmountFromHandler = useCallback(
    (amount: AnyNumber) => {
      if (Number(amount) === 0 || Number.isNaN(Number(amount))) {
        setAmountFrom({
          amountFromRaw: 0n,
          amountFromHumanReadable: '0',
          amountFromUsd: '0.00',
        });
        return;
      }

      const decimals = decimalsForCurrency(tokenFrom);
      const amountFromRaw = Big(String(amount)).mul(Big(10).pow(decimals)).toFixed(0);
      const amountFromHumanReadable = Number(convertBigIntToString(amountFromRaw, tokenFrom));

      const l1FirstTokenUsdPrice = getTokenUsdPrice(tokenFrom);

      const amountFromUsd = Big(amountFromHumanReadable).mul(l1FirstTokenUsdPrice).toFixed(FIAT_PRECISION);

      setAmountFrom({
        amountFromRaw: BigInt(amountFromRaw),
        amountFromHumanReadable: amountFromHumanReadable.toString(),
        amountFromUsd,
      });
    },
    [tokenFrom, allPrices]
  );

  const getSwapTransaction = useCallback(
    async (params: GetSwapTransactionParams) => {
      const swapTransaction = await composeSwapTxByAmountFrom({
        amountFrom: String(amountFrom.amountFromRaw),
        toType: tokenTo.toLowerCase() as GetSwapQuotesByAmountFromToType,
        fromType: tokenFrom.toLowerCase() as GetSwapQuotesByAmountFromFromType,
        toWalletAddress: receiverAddress,
        fromWalletAddress: params.fromWalletAddress,
        fromC1ContractId: getContractParams(tokenFrom).L2_CONTRACT_ADDRESS_BASE,
        fromC2ContractId: getContractParams(tokenFrom).L2_CONTRACT_ADDRESS_MAKER,
        toC1ContractId: getContractParams(tokenTo).L2_CONTRACT_ADDRESS_BASE,
        toC2ContractId: getContractParams(tokenTo).L2_CONTRACT_ADDRESS_MAKER,
        preset: getPreset({ toCurrency: tokenTo, l1Recipient: receiverAddress }),
        utxos: params.utxos ? JSON.stringify(params.utxos) : undefined,
        fromPublicKey: params.fromPublicKey,
        lp: liquidityProvider ? liquidityProvider : undefined,
        withAdditionalData: true,
        excludeC1OrdersIds: isBtcLikeCurrency(tokenFrom) ? Array.from(getBlackList(tokenFrom)) : undefined,
        slippageBips: slippageBips,
      });
      return swapTransaction;
    },
    [tokenFrom, tokenTo, amountFrom, liquidityProvider, receiverAddress]
  );

  const addOrderToBlackList = useCallback((currency: Currency, orderId: string) => {
    addToBlackList(currency, orderId);

    void refetch();
  }, []);

  return (
    <SwapContext.Provider
      value={{
        tokenFrom,
        liquidityProvider,
        setLiquidityProvider,
        tokenTo,
        amountFrom,
        swapQuotes: swapQuotesData,
        swapQuotesError: swapQuotesError instanceof Error ? swapQuotesError : null,
        slippageBips,
        setTokenFrom,
        setTokenTo,
        setAmountFrom: setAmountFromHandler,
        isLoadingSwapQuotes,
        receiverAddress,
        setReceiverAddress,
        isFiatInput,
        setIsFiatInput,
        getSwapTransaction,
        isLimitsSatisfied,
        addOrderToBlackList,
        setSlippageBips,
      }}
    >
      {children}
    </SwapContext.Provider>
  );
};
